import { Dispatch } from '@reduxjs/toolkit';
import { logout } from './userStateSlice';

// localStorage keys with these prefixes are preserved across logout, so user
// preferences (e.g. MUI color mode under `mui-mode` / `mui-color-scheme-*`)
// survive sign-out and the next sign-in honors them.
const PRESERVED_LOCAL_STORAGE_PREFIXES = ['mui-'];

/**
 * Clears localStorage but preserves user-preference keys (currently MUI color
 * mode). Use this instead of `localStorage.clear()` in logout/session-expiry
 * paths so saved preferences persist across sign-ins.
 */
export const clearLocalStoragePreservingPreferences = () => {
  const preserved: Array<[string, string]> = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key &&
      PRESERVED_LOCAL_STORAGE_PREFIXES.some((p) => key.startsWith(p))
    ) {
      const value = localStorage.getItem(key);
      if (value !== null) preserved.push([key, value]);
    }
  }
  localStorage.clear();
  for (const [key, value] of preserved) {
    localStorage.setItem(key, value);
  }
};

/**
 * Performs a complete logout:
 * - Calls backend logout endpoint
 * - Removes login_token cookie
 * - Clears Redux state
 * - Clears localStorage (preserving user preferences) and sessionStorage
 * - Redirects to sign-in page
 */
export const performLogout = async (
  dispatch: Dispatch,
  removeCookie: (name: string, options?: any) => void
) => {
  try {
    await fetch('/api/logout', {
      method: 'GET',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    removeCookie('login_token', { path: '/' });
    dispatch(logout());
    clearLocalStoragePreservingPreferences();
    sessionStorage.clear();
    window.location.href = '/sign-in';
  }
};
